import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { _ as _to_property_key } from "@swc/helpers/_/_to_property_key";
import { jsx as _jsx } from "react/jsx-runtime";
import React from 'react';
import { AiFilterChip } from '~/components/AiSearchChips/AiFilterChip/AiFilterChip';
import { checkboxesToSet, nameSelectionHandler, setToCheckboxes } from '~/components/AiSearchChips/AiFilterChip/chipHandlers';
import { presentCollectionQuantifiers } from '~/components/AiSearchChips/collection-predicate';
import { CollectionChips } from '~/components/AiSearchChips/CollectionChips/CollectionChips';
import { dateRangeChipUnit } from '~/components/AiSearchChips/DateRangeChip/dateRangeChipUnit';
import { modifiedByChipUnit } from '~/components/AiSearchChips/modifiedByChipUnit';
import { presentQuantifiers } from '~/components/AiSearchChips/ServicesChips/service-predicate';
import { isServiceChipField } from '~/components/AiSearchChips/ServicesChips/serviceGrammar';
import { ServicesChips } from '~/components/AiSearchChips/ServicesChips/ServicesChips';
import { SettingsChips } from '~/components/AiSearchChips/SettingsChips/SettingsChips';
import { renderedSettingsFields } from '~/components/AiSearchChips/SettingsChips/settingsGrammar';
import { SettingsChipToneProvider } from '~/components/AiSearchChips/settingsChipTone';
import { currentValue } from '~/components/AiSearchChips/StringMatchChip/StringMatchChip.utils';
import { lastBilledModeOf, lastBilledSummary } from './agreementLastBilled';
import { AgreementOrGroup } from './AgreementOrGroup/AgreementOrGroup';
const SERVICE_QUANTIFIERS = [
    'any',
    'all',
    'none'
];
export const activeServiceQuantifier = (services)=>SERVICE_QUANTIFIERS.find((quantifier)=>services[quantifier] != null);
const dateRangeChipTexts = (texts)=>_object_spread_props(_object_spread({}, texts.dateRange), {
        apply: texts.applyText,
        all: texts.setValue.all
    });
export const buildPredicateCallbacks = (predicate, onChange)=>{
    const setField = (key, value)=>{
        if (value == null) {
            const { [key]: _omit } = predicate, rest = _object_without_properties(predicate, [
                _to_property_key(key)
            ]);
            onChange(rest);
        } else {
            onChange(_object_spread_props(_object_spread({}, predicate), {
                [key]: value
            }));
        }
    };
    return {
        onStatusChange: (value)=>setField('status', value),
        onPaymentMethodChange: (value)=>setField('paymentMethod', value),
        onAmendmentStatusChange: (value)=>setField('amendmentStatus', value),
        onHasCreditChange: (value)=>setField('hasCredit', value),
        onEffectiveDateChange: (value)=>setField('effectiveDate', value),
        onApprovalDateChange: (value)=>setField('approvalDate', value),
        onClientNameChange: (value)=>setField('clientName', value),
        onAgreementTitleChange: (value)=>setField('agreementTitle', value),
        onContactNameChange: (value)=>setField('contactName', value),
        onAssigneeNameChange: (value)=>setField('assigneeName', value),
        onLastModifiedByChange: (value)=>setField('lastModifiedBy', value),
        onLastBilledChange: (value)=>setField('lastBilled', value),
        onPaymentSettingsChange: (value)=>setField('paymentSettings', value),
        onAgreementSettingsChange: (value)=>setField('agreementSettings', value),
        onServicesChange: (value)=>setField('services', value),
        onBundlesChange: (value)=>setField('bundles', value)
    };
};
const SETTINGS_PREDICATE_FIELDS = [
    'paymentSettings',
    'agreementSettings'
];
const predicateFieldOfChip = (chipKey, insertionOrder)=>{
    var _insertionOrder_find;
    if (chipKey.startsWith('service-')) return 'services';
    if (chipKey.startsWith('bundle-')) return 'bundles';
    if (chipKey === 'settings') return (_insertionOrder_find = insertionOrder.find((key)=>SETTINGS_PREDICATE_FIELDS.includes(key))) !== null && _insertionOrder_find !== void 0 ? _insertionOrder_find : chipKey;
    return chipKey;
};
const buildSettingsChipUnit = (param)=>{
    let { predicate, testIds, callbacks, settingsGrammar } = param;
    const rendered = renderedSettingsFields({
        fields: settingsGrammar.fields,
        paymentSettings: predicate.paymentSettings,
        agreementSettings: predicate.agreementSettings
    });
    if (rendered.length === 0) return [];
    return [
        {
            key: 'settings',
            node: /*#__PURE__*/ _jsx(SettingsChips, {
                testId: testIds.Settings,
                grammar: settingsGrammar,
                paymentSettings: predicate.paymentSettings,
                agreementSettings: predicate.agreementSettings,
                onPaymentSettingsChange: callbacks.onPaymentSettingsChange,
                onAgreementSettingsChange: callbacks.onAgreementSettingsChange
            })
        }
    ];
};
export const buildAgreementPredicateChipUnits = (param)=>{
    let { predicate, testIds, callbacks, groupActions, texts, serviceGrammar, settingsGrammar, collectionGrammar, autoOpenKey = null } = param;
    const radioTexts = {
        apply: texts.applyText,
        all: texts.setValue.all
    };
    const units = [];
    if (predicate.status != null) {
        const { selected, operator } = setToCheckboxes(predicate.status);
        units.push({
            key: 'status',
            node: /*#__PURE__*/ _jsx(AiFilterChip, {
                mode: "checkboxes",
                testId: testIds.Status,
                fieldLabel: texts.labels.status,
                items: texts.options.status,
                selected: selected,
                operator: operator,
                onApply: (next)=>callbacks.onStatusChange(checkboxesToSet(next)),
                onRemove: ()=>callbacks.onStatusChange(undefined),
                autoOpen: autoOpenKey === 'status',
                biPrefix: "agreements_ai_chip_status"
            })
        });
    }
    if (predicate.paymentMethod != null) {
        const { selected, operator } = setToCheckboxes(predicate.paymentMethod);
        units.push({
            key: 'paymentMethod',
            node: /*#__PURE__*/ _jsx(AiFilterChip, {
                mode: "checkboxes",
                testId: testIds.PaymentMethod,
                fieldLabel: texts.labels.paymentMethod,
                items: texts.options.paymentMethod,
                selected: selected,
                operator: operator,
                onApply: (next)=>callbacks.onPaymentMethodChange(checkboxesToSet(next)),
                onRemove: ()=>callbacks.onPaymentMethodChange(undefined),
                autoOpen: autoOpenKey === 'paymentMethod',
                biPrefix: "agreements_ai_chip_payment_method"
            })
        });
    }
    if (predicate.amendmentStatus != null) {
        const { selected, operator } = setToCheckboxes(predicate.amendmentStatus);
        units.push({
            key: 'amendmentStatus',
            node: /*#__PURE__*/ _jsx(AiFilterChip, {
                mode: "checkboxes",
                testId: testIds.AmendmentStatus,
                fieldLabel: texts.labels.amendmentStatus,
                items: texts.options.amendmentStatus,
                selected: selected,
                operator: operator,
                onApply: (next)=>callbacks.onAmendmentStatusChange(checkboxesToSet(next)),
                onRemove: ()=>callbacks.onAmendmentStatusChange(undefined),
                autoOpen: autoOpenKey === 'amendmentStatus',
                biPrefix: "agreements_ai_chip_amendment_status"
            })
        });
    }
    if (predicate.hasCredit != null) {
        const { selected, operator } = setToCheckboxes(predicate.hasCredit);
        units.push({
            key: 'hasCredit',
            node: /*#__PURE__*/ _jsx(AiFilterChip, {
                mode: "checkboxes",
                testId: testIds.HasCredit,
                fieldLabel: texts.labels.hasCredit,
                items: texts.options.hasCredit,
                selected: selected,
                operator: operator,
                onApply: (next)=>callbacks.onHasCreditChange(checkboxesToSet(next)),
                onRemove: ()=>callbacks.onHasCreditChange(undefined),
                autoOpen: autoOpenKey === 'hasCredit',
                biPrefix: "agreements_ai_chip_has_credit"
            })
        });
    }
    if (predicate.effectiveDate != null) {
        units.push(dateRangeChipUnit({
            field: 'effectiveDate',
            predicate: predicate.effectiveDate,
            testId: testIds.EffectiveDate,
            label: texts.labels.effectiveDate,
            texts: dateRangeChipTexts(texts),
            onChange: callbacks.onEffectiveDateChange,
            biPrefix: 'agreements_ai_chip_effective_date',
            autoOpen: autoOpenKey === 'effectiveDate'
        }));
    }
    if (predicate.approvalDate != null) {
        units.push(dateRangeChipUnit({
            field: 'approvalDate',
            predicate: predicate.approvalDate,
            testId: testIds.ApprovalDate,
            label: texts.labels.approvalDate,
            texts: dateRangeChipTexts(texts),
            onChange: callbacks.onApprovalDateChange,
            biPrefix: 'agreements_ai_chip_approval_date'
        }));
    }
    if (predicate.clientName != null) {
        const clientName = predicate.clientName;
        const current = currentValue(clientName);
        units.push({
            key: 'clientName',
            node: /*#__PURE__*/ _jsx(AiFilterChip, {
                mode: "radio",
                testId: testIds.ClientName,
                fieldLabel: texts.labels.clientName,
                items: texts.options.clientName,
                selected: current !== '' ? current : null,
                onApply: nameSelectionHandler({
                    current,
                    predicate: clientName,
                    onChange: callbacks.onClientNameChange
                }),
                onRemove: ()=>callbacks.onClientNameChange(undefined),
                biPrefix: "agreements_ai_chip_client_name",
                texts: radioTexts
            })
        });
    }
    if (predicate.agreementTitle != null) {
        const agreementTitle = predicate.agreementTitle;
        const current = currentValue(agreementTitle);
        units.push({
            key: 'agreementTitle',
            node: /*#__PURE__*/ _jsx(AiFilterChip, {
                mode: "radio",
                testId: testIds.AgreementTitle,
                fieldLabel: texts.labels.agreementTitle,
                items: texts.options.agreementTitle,
                selected: current !== '' ? current : null,
                onApply: nameSelectionHandler({
                    current,
                    predicate: agreementTitle,
                    onChange: callbacks.onAgreementTitleChange
                }),
                onRemove: ()=>callbacks.onAgreementTitleChange(undefined),
                biPrefix: "agreements_ai_chip_agreement_title",
                texts: radioTexts
            })
        });
    }
    if (predicate.contactName != null) {
        const contactName = predicate.contactName;
        const current = currentValue(contactName);
        units.push({
            key: 'contactName',
            node: /*#__PURE__*/ _jsx(AiFilterChip, {
                mode: "radio",
                testId: testIds.ContactName,
                fieldLabel: texts.labels.contactName,
                items: texts.options.contactName,
                selected: current !== '' ? current : null,
                onApply: nameSelectionHandler({
                    current,
                    predicate: contactName,
                    onChange: callbacks.onContactNameChange
                }),
                onRemove: ()=>callbacks.onContactNameChange(undefined),
                biPrefix: "agreements_ai_chip_contact_name",
                texts: radioTexts
            })
        });
    }
    if (predicate.assigneeName != null) {
        const assigneeName = predicate.assigneeName;
        const current = currentValue(assigneeName);
        units.push({
            key: 'assigneeName',
            node: /*#__PURE__*/ _jsx(AiFilterChip, {
                mode: "radio",
                testId: testIds.Assignee,
                fieldLabel: texts.labels.assignee,
                items: texts.options.assignee,
                selected: current !== '' ? current : null,
                onApply: nameSelectionHandler({
                    current,
                    predicate: assigneeName,
                    onChange: callbacks.onAssigneeNameChange
                }),
                onRemove: ()=>callbacks.onAssigneeNameChange(undefined),
                biPrefix: "agreements_ai_chip_assignee",
                texts: radioTexts
            })
        });
    }
    if (predicate.lastModifiedBy != null) {
        units.push(modifiedByChipUnit({
            predicate: predicate.lastModifiedBy,
            testId: testIds.ModifiedBy,
            texts: _object_spread_props(_object_spread({}, texts.modifiedBy), {
                apply: texts.applyText
            }),
            onChange: callbacks.onLastModifiedByChange,
            biPrefix: 'agreements_ai_chip_last_modified_by',
            lockSentinelActors: true
        }));
    }
    if (predicate.lastBilled != null) {
        const lastBilled = predicate.lastBilled;
        const applyLastBilled = (param)=>{
            let { selected, from, to } = param;
            if (selected === 'ever') return callbacks.onLastBilledChange({
                exists: true
            });
            if (selected === 'never') return callbacks.onLastBilledChange({
                exists: false
            });
            if (selected !== 'range') return callbacks.onLastBilledChange(undefined);
            const next = {};
            if (from != null) next.gte = from;
            if (to != null) next.lte = to;
            callbacks.onLastBilledChange(next.gte == null && next.lte == null ? undefined : next);
        };
        units.push({
            key: 'lastBilled',
            node: /*#__PURE__*/ _jsx(AiFilterChip, {
                mode: "radio",
                testId: testIds.LastBilled,
                fieldLabel: texts.labels.lastBilled,
                items: [
                    {
                        value: 'all',
                        label: texts.lastBilled.all
                    },
                    {
                        value: 'ever',
                        label: texts.lastBilled.everBilled
                    },
                    {
                        value: 'never',
                        label: texts.lastBilled.neverBilled
                    },
                    {
                        value: 'range',
                        label: texts.lastBilled.specificRange
                    }
                ],
                selected: lastBilledModeOf(lastBilled),
                editable: lastBilled.eq == null && lastBilled.neq == null,
                valueLabel: lastBilledSummary(lastBilled, {
                    allChip: texts.lastBilled.allChip,
                    everBilled: texts.lastBilled.everBilled,
                    neverBilled: texts.lastBilled.neverBilled,
                    specificRange: texts.lastBilled.specificRange
                }),
                datePicker: {
                    rangeValue: 'range',
                    from: lastBilled.gte,
                    to: lastBilled.lte,
                    texts: {
                        from: texts.lastBilled.from,
                        to: texts.lastBilled.to
                    }
                },
                onApply: applyLastBilled,
                onRemove: ()=>callbacks.onLastBilledChange(undefined),
                autoOpen: autoOpenKey === 'lastBilled',
                biPrefix: "agreements_ai_chip_last_billed",
                texts: {
                    apply: texts.applyText,
                    all: texts.lastBilled.all
                }
            })
        });
    }
    units.push(...buildSettingsChipUnit({
        predicate,
        testIds,
        callbacks,
        settingsGrammar
    }));
    if (predicate.services != null && presentQuantifiers(predicate.services).length > 0) {
        units.push({
            key: 'service-chips',
            node: /*#__PURE__*/ _jsx(SettingsChipToneProvider, {
                value: "default",
                children: /*#__PURE__*/ _jsx(ServicesChips, {
                    testId: testIds.Services,
                    grammar: serviceGrammar,
                    services: predicate.services,
                    autoOpenField: isServiceChipField(autoOpenKey) ? autoOpenKey : undefined,
                    onChange: callbacks.onServicesChange
                })
            })
        });
    }
    if (predicate.bundles != null && presentCollectionQuantifiers(predicate.bundles).length > 0) {
        units.push({
            key: 'bundle-chips',
            node: /*#__PURE__*/ _jsx(SettingsChipToneProvider, {
                value: "default",
                children: /*#__PURE__*/ _jsx(CollectionChips, {
                    testId: testIds.BundleName,
                    fieldLabel: texts.labels.bundleName,
                    nounLabel: texts.bundleNoun(),
                    collection: predicate.bundles,
                    grammar: collectionGrammar,
                    onChange: callbacks.onBundlesChange
                })
            })
        });
    }
    const groupKey = predicate.or != null ? 'or' : 'and';
    const groupArms = predicate[groupKey];
    if (groupArms != null) {
        units.push({
            key: groupKey,
            node: /*#__PURE__*/ _jsx(AgreementOrGroup, {
                testId: testIds.OrGroup,
                arms: groupArms,
                tone: groupKey,
                texts: texts,
                serviceGrammar: serviceGrammar,
                onArmChange: groupActions.onArmChange,
                onClearGroup: groupActions.onClearGroup,
                onToggleConnector: groupActions.onToggleConnector
            })
        });
    }
    const insertionOrder = Object.keys(predicate);
    const orderIndex = (chipKey)=>{
        if (chipKey === 'status') return -1;
        const index = insertionOrder.indexOf(predicateFieldOfChip(chipKey, insertionOrder));
        return index === -1 ? insertionOrder.length : index;
    };
    return units.map((unit, index)=>({
            unit,
            index
        })).sort((a, b)=>orderIndex(a.unit.key) - orderIndex(b.unit.key) || a.index - b.index).map((entry)=>entry.unit);
};
